import { useState, useEffect, useRef, useCallback } from "react";
import {
  Accordion,
  AccordionItem,
  type ListboxProps,
  type ListboxSectionProps,
  type Selection,
  Avatar,
  Button,
} from "@heroui/react";
import React from "react";
import {
  Listbox,
  Tooltip,
  ListboxItem,
  ListboxSection,
} from "@heroui/react";
import { cn } from "@heroui/react";
import { useLocale, useTranslations } from "next-intl";
import { Icon } from "@iconify/react";

import { signOut } from "@/app/[locale]/login/actions";
import { useRouter } from "@/i18n/routing";

import ChevronLeftIcon from "@/components/Icons/ChevronLeftIcon";
import ChevronRightIcon from "@/components/Icons/ChevronRightIcon";

export enum SidebarItemType {
  Nest = "nest",
}

export type SidebarItem = {
  key: string;
  title: string;
  icon?: string;
  href?: string;
  type?: SidebarItemType.Nest;
  startContent?: React.ReactNode;
  endContent?: React.ReactNode;
  items?: SidebarItem[];
  className?: string;
};

export type SidebarProps = Omit<ListboxProps<SidebarItem>, "children"> & {
  items: SidebarItem[];
  isCompact?: boolean;
  hideEndContent?: boolean;
  iconClassName?: string;
  sectionClasses?: ListboxSectionProps["classNames"];
  classNames?: ListboxProps["classNames"];
  defaultSelectedKey: string;
  onSelect?: (key: string) => void;
  user?: any;
};

const Sidebar = React.forwardRef<HTMLElement, SidebarProps>(
  (
    {
      items,
      isCompact,
      defaultSelectedKey,
      onSelect,
      hideEndContent,
      sectionClasses: sectionClassesProp = {},
      itemClasses: itemClassesProp = {},
      iconClassName,
      classNames,
      className,
      user,
      ...props
    },
    ref,
  ) => {
    const [selected, setSelected] = useState<React.Key>(defaultSelectedKey);
    const [isHidden, setIsHidden] = useState<boolean>(false);

    const router = useRouter();
    const locale = useLocale();
    const buttonRef = useRef(null);
    const t = useTranslations("Sidebar");

    const sectionClasses = {
      ...sectionClassesProp,
      base: cn(sectionClassesProp?.base, "w-full", {
        "p-0 max-w-[44px]": isCompact,
      }),
      group: cn(sectionClassesProp?.group, {
        "flex flex-col gap-1": isCompact,
      }),
      heading: cn(sectionClassesProp?.heading, {
        hidden: isCompact,
      }),
    };

    const handleKeyPress = useCallback((event) => {
      // Example: Ctrl + B to trigger the button
      if (
        (event.ctrlKey && event.key === "b") ||
        (event.metaKey && event.key === "b")
      ) {
        if (buttonRef.current) {
          buttonRef.current.click(); // Programmatically click the button
        }
      }
    }, []);

    async function handleSignIn() {
      router.push("/login");
    }

    async function handleSignOut() {
      await signOut();
      router.push("/");
    }

    // Step 3: Add and clean up the keypress event listener
    useEffect(() => {
      window.addEventListener("keydown", handleKeyPress);

      return () => {
        window.removeEventListener("keydown", handleKeyPress); // Clean up
      };
    }, [handleKeyPress]);

    const itemClasses = {
      ...itemClassesProp,
      base: cn(itemClassesProp?.base, {
        "w-11 h-11 gap-0 p-0": isCompact,
      }),
    };

    const renderNestItem = React.useCallback(
      (item: SidebarItem) => {
        const isNestType =
          item.items &&
          item.items?.length > 0 &&
          item?.type === SidebarItemType.Nest;

        if (isNestType) {
          // Is a nest type item , so we need to remove the href
          delete item.href;
        }

        return (
          <ListboxItem
            {...item}
            key={item.key}
            href={`/${locale}/${item.href}`}
            classNames={{
              base: cn(
                {
                  "h-auto p-0": !isCompact && isNestType,
                },
                {
                  "inline-block w-11": isCompact && isNestType,
                },
              ),
            }}
            endContent={
              isCompact || isNestType || hideEndContent
                ? null
                : (item.endContent ?? null)
            }
            startContent={
              isCompact || isNestType ? null : item.icon ? (
                <Icon
                  className={cn(
                    "text-default-500 group-data-[selected=true]:text-foreground",
                    iconClassName,
                  )}
                  icon={item.icon}
                  width={24}
                />
              ) : (
                (item.startContent ?? null)
              )
            }
            title={isCompact || isNestType ? null : t(item.title)}
          >
            {isCompact ? (
              <Tooltip content={t(item.title)} placement="right">
                <div className="flex w-full items-center justify-center">
                  {item.icon ? (
                    <Icon
                      className={cn(
                        "text-default-500 group-data-[selected=true]:text-foreground",
                        iconClassName,
                      )}
                      icon={item.icon}
                      width={24}
                    />
                  ) : (
                    (item.startContent ?? null)
                  )}
                </div>
              </Tooltip>
            ) : null}

            {!isCompact && isNestType ? (
              <Accordion className={"p-0"}>
                <AccordionItem
                  key={item.key}
                  aria-label={t(item.title)}
                  classNames={{
                    heading: "pr-3",
                    trigger: "p-0",
                    content: "py-0 pl-4",
                  }}
                  title={
                    item.icon ? (
                      <div
                        className={"flex h-11 items-center gap-2 px-2 py-1.5"}
                      >
                        <Icon
                          className={cn(
                            "text-default-500 group-data-[selected=true]:text-foreground",
                            iconClassName,
                          )}
                          icon={item.icon}
                          width={24}
                        />
                        <span className="text-small font-medium text-default-500 group-data-[selected=true]:text-foreground">
                          {t(item.title)}
                        </span>
                      </div>
                    ) : (
                      (item.startContent ?? null)
                    )
                  }
                >
                  {item.items && item.items?.length > 0 ? (
                    <Listbox
                      className={"mt-0.5"}
                      classNames={{
                        list: cn("border-l border-default-200 pl-4"),
                      }}
                      items={item.items}
                      variant="flat"
                    >
                      {item.items.map(renderItem)}
                    </Listbox>
                  ) : (
                    renderItem(item)
                  )}
                </AccordionItem>
              </Accordion>
            ) : null}
          </ListboxItem>
        );
      },
      [isCompact, hideEndContent, iconClassName, items],
    );

    const renderItem = React.useCallback(
      (item: SidebarItem) => {
        const isNestType =
          item.items &&
          item.items?.length > 0 &&
          item?.type === SidebarItemType.Nest;

        if (isNestType) {
          return renderNestItem(item);
        }

        return (
          <ListboxItem
            {...item}
            key={item.key}
            href={`/${locale}/${item.href}`}
            endContent={
              isCompact || hideEndContent ? null : (item.endContent ?? null)
            }
            startContent={
              isCompact ? null : item.icon ? (
                <Icon
                  className={cn(
                    "text-default-500 group-data-[selected=true]:text-foreground",
                    iconClassName,
                  )}
                  icon={item.icon}
                  width={24}
                />
              ) : (
                (item.startContent ?? null)
              )
            }
            textValue={t(item.title)}
            title={isCompact ? null : t(item.title)}
          >
            {isCompact ? (
              <Tooltip content={t(item.title)} placement="right">
                <div className="flex w-full items-center justify-center">
                  {item.icon ? (
                    <Icon
                      className={cn(
                        "text-default-500 group-data-[selected=true]:text-foreground",
                        iconClassName,
                      )}
                      icon={item.icon}
                      width={24}
                    />
                  ) : (
                    (item.startContent ?? null)
                  )}
                </div>
              </Tooltip>
            ) : null}
          </ListboxItem>
        );
      },
      [isCompact, hideEndContent, iconClassName, itemClasses?.base],
    );

    return (
      <>
        <div
          className={cn(className, {
            "-mr-[15rem] opacity-0 md:-ml-[15rem]": isHidden,
          })}
        >
          <div className="flex items-center gap-3 px-4">
            {user && user?.user_metadata?.avatar_url ? (
              <>
                <Avatar src={user?.user_metadata?.avatar_url} />
                {true && (
                  <div className="flex flex-col">
                    <p className="text-small font-medium text-primary">
                      {user?.user_metadata?.name}
                    </p>
                  </div>
                )}
              </>
            ) : (
              <span></span>
            )}
          </div>
          <Listbox
            key={isCompact ? "compact" : "default"}
            ref={ref}
            hideSelectedIcon
            as="nav"
            className={cn("list-none")}
            classNames={{
              list: cn("items-center", classNames?.list),
            }}
            color="default"
            itemClasses={{
              ...itemClasses,
              base: cn(
                "px-3 min-h-11 rounded-large h-[44px] data-[selected=true]:bg-default-100",
                itemClasses?.base,
              ),
              title: cn(
                "text-small font-medium text-default-500 group-data-[selected=true]:text-foreground",
                itemClasses?.title,
              ),
            }}
            items={items}
            selectedKeys={[selected] as unknown as Selection}
            selectionMode="single"
            variant="flat"
            onSelectionChange={(keys) => {
              const key = Array.from(keys)[0];

              setSelected(key as React.Key);
              onSelect?.(key as string);
            }}
            {...props}
          >
            {(item) => {
              return item.items &&
                item.items?.length > 0 &&
                item?.type === SidebarItemType.Nest ? (
                renderNestItem(item)
              ) : item.items && item.items?.length > 0 ? (
                <ListboxSection
                  key={item.key}
                  classNames={sectionClasses}
                  showDivider={isCompact}
                  title={t(item.title)}
                  className="text-primary"
                >
                  {item.items.map(renderItem)}
                </ListboxSection>
              ) : (
                renderItem(item)
              );
            }}
          </Listbox>
          <div className="flex flex-col">
            {user ? (
              <Button
                className="justify-start text-default-500 data-[hover=true]:text-foreground"
                variant="light"
                color="danger"
                onClick={() => handleSignOut()}
              >
                {t("sign-out")}
              </Button>
            ) : (
              <Button
                className="justify-start text-default-500 data-[hover=true]:text-foreground"
                variant="light"
                color="danger"
                onClick={() => handleSignIn()}
              >
                {t("sign-in")}
              </Button>
            )}
          </div>
        </div>
        <div
          className={cn(
            "border-rounded-full sticky z-[20] flex h-screenNav flex-col items-center justify-center transition-all md:bg-background-900/5 md:px-4 md:backdrop-blur-lg",
            { "md:ml-[15rem]": isHidden },
          )}
        >
          <div className="h-inherit mr-[3rem] mt-[29vh] rounded-full bg-background shadow-lg md:ml-[0rem] md:mr-[0rem] md:mt-[0rem] md:rounded-none md:bg-transparent md:shadow-none">
            <Button
              ref={buttonRef}
              isIconOnly
              variant="light"
              onPress={() => setIsHidden(!isHidden)}
              aria-label="toggle-sidebar"
            >
              {isHidden ? (
                <ChevronLeftIcon filled fill={"#6A488E"} />
              ) : (
                <ChevronRightIcon filled fill={"#6A488E"} />
              )}
            </Button>
          </div>
        </div>
      </>
    );
  },
);

Sidebar.displayName = "Sidebar";

export default Sidebar;
